/**
 * Static content service - fetches pre-generated JSON files from GCS
 * Minimizes Firestore reads by using cached static data
 */

const STATIC_BASE_URL = import.meta.env.VITE_STATIC_CONTENT_URL ||
  'https://storage.googleapis.com/foxie-systems-dev.firebasestorage.app/static';

/**
 * Cache for static content with TTL
 */
interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

const cache = new Map<string, CacheEntry<any>>();

/**
 * Fetch static JSON file from GCS with caching
 */
async function fetchStatic<T>(filename: string, cacheTTL: number = 3600000): Promise<T> {
  const now = Date.now();
  const cached = cache.get(filename);

  // Return cached data if still valid
  if (cached && cached.expiresAt > now) {
    console.log(`[StaticContent] Cache hit: ${filename}`);
    return cached.data;
  }

  console.log(`[StaticContent] Fetching: ${filename}`);

  try {
    const response = await fetch(`${STATIC_BASE_URL}/${filename}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch ${filename}: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Store in cache
    cache.set(filename, {
      data,
      expiresAt: now + cacheTTL,
    });

    return data;
  } catch (error) {
    console.error(`[StaticContent] Error fetching ${filename}:`, error);

    // If we have expired cache, return it as fallback
    if (cached) {
      console.warn(`[StaticContent] Using expired cache for ${filename}`);
      return cached.data;
    }

    throw error;
  }
}

/**
 * Category data structure
 */
export interface CategoryData {
  id: string;
  name: string;
  description: string;
  icon: string;
}

/**
 * Product summary structure (from static file)
 */
export interface ProductSummary {
  id: string;
  title: string;
  shortDescription: string;
  category: string;
  qualityTier: string;
  price: {
    amount: number;
    currency: string;
  };
  imageUrl?: string;
  publishedAt?: string;
}

/**
 * Static content manifest
 */
export interface StaticManifest {
  version: string;
  generatedAt: any;
  files: {
    categories: string;
    productsSummary: string;
    featuredProducts: string;
  };
}

/**
 * Get categories from static JSON
 */
export async function getCategories(): Promise<CategoryData[]> {
  return fetchStatic<CategoryData[]>('categories.json', 86400000); // 24 hour cache
}

/**
 * Get products summary from static JSON
 */
export async function getProductsSummary(): Promise<ProductSummary[]> {
  return fetchStatic<ProductSummary[]>('products-summary.json', 3600000); // 1 hour cache
}

/**
 * Get featured products from static JSON
 */
export async function getFeaturedProducts(): Promise<ProductSummary[]> {
  return fetchStatic<ProductSummary[]>('featured-products.json', 3600000); // 1 hour cache
}

/**
 * Get static content manifest
 */
export async function getManifest(): Promise<StaticManifest> {
  return fetchStatic<StaticManifest>('manifest.json', 300000); // 5 minute cache
}

/**
 * Clear all static content cache (useful for testing)
 */
export function clearStaticCache(): void {
  cache.clear();
  console.log('[StaticContent] Cache cleared');
}

/**
 * Filter products by category
 */
export function filterProductsByCategory(products: ProductSummary[], category?: string): ProductSummary[] {
  if (!category) return products;
  return products.filter(p => p.category === category);
}

/**
 * Sort products by field
 */
export function sortProducts(
  products: ProductSummary[],
  sortBy: 'price' | 'title' | 'publishedAt' = 'publishedAt',
  order: 'asc' | 'desc' = 'desc'
): ProductSummary[] {
  const sorted = [...products].sort((a, b) => {
    let aVal: any;
    let bVal: any;

    switch (sortBy) {
      case 'price':
        aVal = a.price.amount;
        bVal = b.price.amount;
        break;
      case 'title':
        aVal = a.title.toLowerCase();
        bVal = b.title.toLowerCase();
        break;
      case 'publishedAt':
        aVal = a.publishedAt || '';
        bVal = b.publishedAt || '';
        break;
    }

    if (order === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  return sorted;
}
